import { useMemo } from "react";
import { Badge } from "@/core/components/ui/badge";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { formatMoney, formatMoneyCents } from "@/core/utils/money";
import { cn } from "@/core/utils/utils";
import { SpiffRule } from "@/spiff-rules/spiff-rule";
import { getSpiffRuleDisplay } from "@/spiff-rules/utils";
import { SPIFF_EVENT_BADGE_VARIANTS } from "@/spiffs/constants";

interface SpiffRuleListItemProps {
  spiffRule: SpiffRule;
  isLastItem?: boolean;
}

const formatSpiffRuleAmount = (rule: SpiffRule) => {
  if (rule.payment_amount_cents) {
    return formatMoney(rule.payment_amount_cents / 100);
  }
  if (rule.payment_percent) {
    return `${rule.payment_percent}%`;
  }
  return "$0";
};

export const SpiffRuleListItem = ({
  spiffRule,
  isLastItem,
}: SpiffRuleListItemProps) => {
  const { label, description } = useMemo(
    () => getSpiffRuleDisplay(spiffRule),
    [spiffRule],
  );

  const badgeVariant =
    SPIFF_EVENT_BADGE_VARIANTS[
      spiffRule.event as keyof typeof SPIFF_EVENT_BADGE_VARIANTS
    ];

  return (
    <Stack
      direction="row"
      align="center"
      justify="between"
      gap="md"
      fullWidth
      className={cn("py-4", {
        "border-b border-gray-200": !isLastItem,
      })}
    >
      <Stack gap="xs">
        <Typography variant="subtitleW2">{label}</Typography>
        <Typography variant="bodyW2" textColor="secondary">
          {description}
        </Typography>
      </Stack>
      <Stack gap="xs" align="end">
        <Badge variant={badgeVariant} size="lg">
          {formatSpiffRuleAmount(spiffRule)}
        </Badge>
        {spiffRule.payment_percent && spiffRule.payment_percent_cap_cents && (
          <Typography
            variant="captionW1"
            className="text-text-secondary w-fit whitespace-nowrap"
          >
            {formatMoneyCents(spiffRule.payment_percent_cap_cents)} max.
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};
